/**
 * Optimización de imágenes remotas de tours (Bokun S3) vía CDN de imágenes.
 *
 * Los originales de `bokun.s3.amazonaws.com` se sirven sin `Cache-Control`,
 * sin redimensionar y en JPEG/PNG (80–150 KB por tarjeta). El proxy devuelve
 * WebP redimensionado al ancho realmente pintado y con `max-age=31536000`,
 * lo que reduce ~80% el peso y elimina descargas repetidas.
 *
 * No se toca el diseño: mismas dimensiones, mismo recorte (`fit=cover`).
 */

const CDN = "https://wsrv.nl/";

/** Hosts remotos que sabemos que el proxy puede leer. */
const PROXYABLE_HOSTS = [
  "bokun.s3.amazonaws.com",
  "s3.amazonaws.com",
  "images.bokun.is",
  "bokun.imgix.net",
];

/** Anchos ofrecidos en el `srcset` de una tarjeta (DPR incluido). */
export const CARD_WIDTHS = [320, 420, 540, 700] as const;

/**
 * Calidad decreciente con el ancho: en pantallas retina el escalado a la
 * mitad oculta la pérdida, así que los candidatos grandes pesan menos sin
 * que se note en la tarjeta.
 */
function qualityFor(width: number, base?: number): number {
  if (base) return base;
  if (width >= 700) return 62;
  if (width >= 480) return 68;
  return 74;
}


/** `sizes` para la rejilla de tarjetas: 1 col móvil, 2 sm, 3–4 desktop. */
export const CARD_SIZES =
  "(min-width: 1280px) 300px, (min-width: 1024px) 24vw, (min-width: 640px) 45vw, 92vw";

function isProxyable(url: string): boolean {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return false;
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    const host = new URL(url).hostname.toLowerCase();
    return PROXYABLE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

/**
 * Devuelve la URL optimizada (WebP, ancho `width`) o la original si el host
 * no es proxyable (assets locales, CDN de Lovable, data URIs).
 */
export function cdnImage(
  url: string | null | undefined,
  width: number,
  opts: { quality?: number; height?: number } = {},
): string {
  if (!url) return "";
  if (!isProxyable(url)) return url;
  const params = new URLSearchParams();
  // El proxy acepta la URL sin esquema.
  params.set("url", url.replace(/^https?:\/\//i, ""));
  params.set("w", String(Math.round(width)));
  if (opts.height) params.set("h", String(Math.round(opts.height)));
  params.set("fit", "cover");
  params.set("output", "webp");
  params.set("q", String(qualityFor(width, opts.quality)));
  // `we` = no ampliar más allá del original (evita bytes inútiles).
  params.set("we", "");
  return `${CDN}?${params.toString()}`;
}

/** `srcset` responsivo. Cadena vacía si la imagen no es optimizable. */
export function cdnSrcSet(
  url: string | null | undefined,
  widths: readonly number[] = CARD_WIDTHS,
  opts: { quality?: number; aspect?: number } = {},
): string | undefined {
  if (!url || !isProxyable(url)) return undefined;
  return widths
    .map((w) => {
      const height = opts.aspect ? Math.round(w / opts.aspect) : undefined;
      return `${cdnImage(url, w, { quality: opts.quality, height })} ${w}w`;
    })
    .join(", ");
}
